import React from 'react';
import { ArrowLeft, User, Edit2, CheckCircle, XCircle } from 'lucide-react';
import Button from './ui/button';

const TicketDetailHeader = ({
  ticketNumber,
  status,
  subject,
  customerName,
  isEditing,
  canUpdate,
  canResolve,
  canClose,
  submitting,
  onBack,
  onEdit,
  onCancelEdit,
  onSave,
  onResolve,
  onClose
}) => {
  const getStatusStyles = (currentStatus) => {
    const s = String(currentStatus || '').toLowerCase();
    switch (s) {
      case 'open':
      case 'new':
        return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 border-yellow-200';
      case 'in_progress':
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-200';
      case 'resolved':
        return 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-emerald-200';
      case 'closed':
        return 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  return (
    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
      <div className="flex items-start gap-4 min-w-0">
        <button
          onClick={onBack}
          className="mt-1 p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-all shrink-0"
          aria-label="Go back"
        >
          <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-300" />
        </button>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-xs font-mono font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 px-2 py-0.5 rounded border border-indigo-100 dark:border-indigo-800">
              #{ticketNumber}
            </span>
            <span className="text-slate-300 dark:text-slate-600">•</span>
            <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] uppercase font-bold tracking-wider ${getStatusStyles(status)}`}>
              <div className={`w-1.5 h-1.5 rounded-full ${status === 'open' ? 'bg-yellow-500 animate-pulse' : 'bg-current'}`} />
              {status?.replace('_', ' ')}
            </div>
          </div>

          <h1
            className="text-xl md:text-2xl font-bold tracking-tight text-slate-900 dark:text-white truncate max-w-2xl"
            title={subject}
          >
            {subject}
          </h1>

          <div className="flex items-center gap-3 mt-1">
            <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1.5 font-medium">
              <User className="w-3.5 h-3.5" />
              {customerName}
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {!isEditing ? (
          <>
            {canUpdate && (
              <Button
                variant="unstyled"
                onClick={onEdit}
                className="h-10 px-5 text-sm font-semibold rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:opacity-90 shadow-md flex items-center gap-2"
              >
                <Edit2 className="w-4 h-4" /> Update
              </Button>
            )}
            {canResolve && (
              <Button
                variant="unstyled"
                onClick={onResolve}
                className="h-10 px-5 text-sm font-semibold rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 shadow-md flex items-center gap-2"
              >
                <CheckCircle className="w-4 h-4" /> Resolve
              </Button>
            )}
            {canClose && (
              <Button
                variant="unstyled"
                onClick={onClose}
                className="h-10 px-5 text-sm font-semibold rounded-xl bg-slate-700 text-white hover:bg-slate-800 shadow-md flex items-center gap-2"
              >
                <XCircle className="w-4 h-4" /> Close
              </Button>
            )}
          </>
        ) : (
          <div className="flex gap-2 bg-slate-100 dark:bg-slate-800 p-1.5 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-inner">
            <Button
              variant="unstyled"
              onClick={onCancelEdit}
              className="h-8 px-4 text-xs font-bold text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
            >
              Cancel
            </Button>
            <Button
              variant="unstyled"
              onClick={onSave}
              disabled={submitting}
              className="h-8 px-4 text-xs font-bold bg-indigo-600 text-white rounded-lg shadow-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {submitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketDetailHeader;
